import logger from '@finn-no/fiaas-logger';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useConfig } from 'shared/config/ConfigContext';
import { fetchVariants } from 'shared/experiment/variants';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { Hotjar } from 'shared/util/hotjar';
import { fetchSearchId } from '@/api/apiInternal';
import { ExperimentProvider, type FhhExperiments } from '@/hooks/experiments';
import { useStoreExperimentVariant } from '@/hooks/useStoreExperimentVariant';
import { ToastProvider } from '@/scenes/resultPage/context/toast/ToastProvider';
import { ResultPage } from '@/scenes/resultPage/ResultPage';
import { clientConfig, config } from '@/server/config';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import type { CommonPageProps } from '@/types/common';

const EXPERIMENT_KEY = 'tjvr-breach-detection-in-boost';
const EXPERIMENT_TTL_DAYS = 60;

interface ResultatProps extends CommonPageProps {
    searchId: string | null;
    experiments: FhhExperiments;
    source: TrafficSource | null;
}

type TrafficSource = 'request_rejected_system_message';

const Result: NextPage<ResultatProps> = ({ deviceType, loginId, searchId, experiments, unleash, hasLegalBasis, source }) => {
    const { baseUrl } = useConfig();

    useStoreExperimentVariant(EXPERIMENT_KEY, experiments[EXPERIMENT_KEY], EXPERIMENT_TTL_DAYS);
    useStoreExperimentVariant('travel_traffic_source', source, EXPERIMENT_TTL_DAYS);

    const hotjarEnabled = !!(unleash?.hotjarResultList && hasLegalBasis);

    return (
        <ExperimentProvider experiments={experiments}>
            <Head
                title="Feriehjem og hytter - sammenlign pris | FINN reise"
                description="Finn tusenvis av feriehjem og hytter på et sted!"
                canonical={`${baseUrl}/reise/feriehus-hytteutleie/resultat/`}
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie">
                <ToastProvider>
                    <ResultPage deviceType={deviceType} loginId={loginId} searchId={searchId} />
                </ToastProvider>
            </Main>
            <Hotjar enabled={hotjarEnabled} />
        </ExperimentProvider>
    );
};

const hasRequiredSearchParams = (query: GetServerSidePropsContext['query']): boolean =>
    Boolean(query.booking_from && query.booking_to && query.no_of_beds_from);

export const getServerSideProps = async ({ req, res, query }: GetServerSidePropsContext) => {
    const commonProps = await buildCommonProps(req, res, query);

    const fetchedVariants = await fetchVariants(req, res, 'travel-fhh-web', config.get('experimentsApiUrl'), [EXPERIMENT_KEY]);
    const experiments: FhhExperiments = fetchedVariants ?? {};
    const source = query.travel_traffic_source === 'request_rejected_system_message' ? 'request_rejected_system_message' : null;

    let searchId: string | null = null;

    if (hasRequiredSearchParams(query)) {
        try {
            const { searchId: fetchedSearchId } = await fetchSearchId(query);
            searchId = fetchedSearchId ?? null;
        } catch (err: unknown) {
            logger.error(String(err));
        }
    }

    return {
        props: {
            config: clientConfig,
            ...sanitizeCommonProps(commonProps),
            searchId,
            experiments,
            source,
        },
    };
};

export default Result;
